import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiUtils } from '@/common/utils';
import type { ExportHistoryKind, ExportHistoryRowData, UseExportHistoryResult } from './types';
import { useExportHistoryEmployee } from './useExportHistoryEmployee';
import { useExportHistoryExternalAccount } from './useExportHistoryExternalAccount';
import { useExportHistoryNumberOfEmployee } from './useExportHistoryNumberOfEmployee';
import { useExportHistoryPerformanceReview } from './useExportHistoryPerformanceReview';
import { useExportHistoryProbationReview } from './useExportHistoryProbationReview';

type LiveProgress = Pick<
  ExportHistoryRowData,
  'status' | 'updatedAt' | 'exportType' | 'totalRequestCount' | 'completedRequestCount'
>;

const emptyExportHistoryResult: UseExportHistoryResult = {
  error: undefined,
  history: [],
  isActive: false,
  loading: false,
  progress: [],
  refetch: () => void 0,
};

export const useExportHistory = (
  kind: ExportHistoryKind,
  onlyMyTask: boolean,
): UseExportHistoryResult & { rows: Array<ExportHistoryRowData> } => {
  const employee = useExportHistoryEmployee(kind, onlyMyTask);
  const numberOfEmployee = useExportHistoryNumberOfEmployee(kind, onlyMyTask);
  const performanceReview = useExportHistoryPerformanceReview(kind, onlyMyTask);
  const externalAccount = useExportHistoryExternalAccount(kind, onlyMyTask);
  const probationReview = useExportHistoryProbationReview(kind, onlyMyTask);

  const [liveProgressById, setLiveProgressById] = useState<Record<string, LiveProgress>>({});
  const previousIncompleteIdsRef = useRef<Set<string>>(new Set());

  const active = useMemo((): UseExportHistoryResult => {
    switch (kind) {
      case 'employee':
        return employee;
      case 'numberOfEmployee':
        return numberOfEmployee;
      case 'performanceReviewList':
      case 'performanceReviewResults':
        return performanceReview;
      case 'externalAccount':
        return externalAccount;
      case 'probationReview':
        return probationReview;
      default:
        return emptyExportHistoryResult;
    }
  }, [kind, employee, numberOfEmployee, performanceReview, externalAccount, probationReview]);

  const progressList = useMemo(() => active.progress, [active.progress]);

  useEffect(() => {
    if (active.error) ApiUtils.handleApiError(active.error);
  }, [active.error]);

  useEffect(() => {
    // A row present in the previous emission but absent from this one has settled (reached
    // DONE/FAILED) - it no longer appears in the incomplete-only subscription payload.
    const currentIds = new Set(progressList.map(item => item.id));
    const settledIds = [...previousIncompleteIdsRef.current].filter(id => !currentIds.has(id));
    previousIncompleteIdsRef.current = currentIds;

    setLiveProgressById(prev => {
      const next = { ...prev };
      for (const item of progressList) {
        next[item.id] = {
          status: item.status,
          updatedAt: item.updatedAt,
          totalRequestCount: item.totalRequestCount,
          completedRequestCount: item.completedRequestCount,
        };
      }
      // Drop settled rows' stale IN_PROGRESS snapshot - otherwise it permanently overrides the
      // refetched row's real terminal status in the `rows` merge below, since this map is never
      // cleared once set.
      for (const id of settledIds) {
        delete next[id];
      }
      return next;
    });

    // Refetch the full history query to pick up the settled row's resultUrl/failureReason,
    // which the lightweight Progress payload never carries.
    if (settledIds.length > 0) {
      active.refetch();
    }
  }, [progressList, active.refetch]);

  const rows: ExportHistoryRowData[] = useMemo(
    () =>
      (active.history ?? [])
        .map(row => ({ ...row, ...liveProgressById[row.id] }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [active.history, liveProgressById],
  );

  // Stable across renders (as long as Apollo's own `refetch` is) - the settle-detection effect in
  // useExportHistory.ts depends on this reference, and a fresh function every render would make
  // that effect re-fire every render regardless of whether progressList actually changed.
  const stableRefetch = useCallback(() => void active.refetch(), [active.refetch]);

  return { ...active, rows, refetch: stableRefetch };
};
